// TODO: Find the correct ElementType and remove the lint ignore
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CSSProperties, ElementType, ReactNode, RefObject, SyntheticEvent } from 'react';
import classNames from 'classnames';
import { capitalize } from 'lodash';
import BootstrapButton from 'react-bootstrap/Button';

import { DynamicTestId } from 'common/element';

import Link from 'components/common/Link/Link';

import './Button.scss';

export type ButtonRef =
  | string
  | ((instance: BootstrapButton<ElementType<any>> | null) => void)
  | RefObject<BootstrapButton<ElementType<any>>>
  | null
  | undefined;

export enum ButtonVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  BORDERLESS = 'borderless',
}

export type ButtonSize = 'sm' | 'lg' | undefined;

export type ButtonPropTypes = {
  active?: boolean;
  variant?: ButtonVariant;
  type?: 'button' | 'submit' | 'reset' | undefined;
  children?: ReactNode;
  size?: ButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  onClick?: (e: SyntheticEvent) => void;
  href?: string;
  buttonRef?: ButtonRef;
  className?: string;
  disabled?: boolean;
  style?: CSSProperties;
  openInNewTab?: boolean;
  testId?: DynamicTestId;
  submitForm?: string;
};

/**
 * Button is a component for creating a Scalyr version of a Bootstrap button.
 * A Button has one or more of: an icon at the left, text, and an icon on the
 * right. The button will be styled based on its variant, which is one of:
 *
 *     'primary':    green with white text (turns dark green on hover)
 *     'secondary':  white with black text, grey border (turns grey on hover)
 *     'borderless': black text, grey border (grey background on hover)
 *
 * @prop active          if true, button will have a dedicated styling for active / selected buttons
 * @prop variant         primary | secondary | borderless
 * @prop type            button, submit, or reset. Default to button
 * @prop children        button content (usually but not always just text)
 * @prop size            sm | lg  use this prop to render a button that's smaller or larger than the default
 * @prop leftIcon        left icon component
 * @prop rightIcon       right icon component
 * @prop onClick         click handler
 * @prop href            render the component as an anchor tag with this href styled as a button
 * @prop buttonRef       ref prop for bootstrap button
 * @prop className       class(es) to add to button
 * @prop disabled        if true, button is disabled and click does nothing
 * @prop style           style object
 * @prop openInNewTab    If true, the link target will open in a new browser tab
 * @prop testId          uniquely identifying string to use for testing
 * @prop submitForm      this is id attribute of a <form> element where the button belongs to - applicable to footer buttons in modal dialog with form
 *
 * @returns {Component}
 */

function Button({
  active,
  variant = ButtonVariant.SECONDARY,
  type,
  size,
  leftIcon,
  rightIcon,
  onClick,
  href,
  buttonRef,
  className,
  disabled,
  children,
  style,
  openInNewTab,
  testId,
  submitForm,
  ...props // This is required for things like tooltips to work
}: ButtonPropTypes): JSX.Element {
  // 'testId' not a valid prop unless we're passing it to Link
  const testIdProp = href ? { testId } : { 'data-test-id': testId };

  const leftIconSpan = leftIcon ? <span className="Button__LeftIcon">{leftIcon}</span> : null;
  const rightIconSpan = rightIcon ? <span className="Button__RightIcon">{rightIcon}</span> : null;

  return (
    <BootstrapButton
      {...props}
      as={href ? Link : undefined}
      variant={variant === ButtonVariant.PRIMARY ? 'primary' : 'secondary'}
      href={href}
      size={size}
      className={classNames('Button', `Button--${capitalize(variant)}`, active ? 'Button--Active' : '', className)}
      onClick={onClick}
      ref={buttonRef}
      type={type}
      disabled={disabled}
      style={style}
      target={openInNewTab ? '_blank' : undefined}
      {...testIdProp}
      form={submitForm}
    >
      {leftIconSpan} {children} {rightIconSpan}
    </BootstrapButton>
  );
}

export default Button;
